// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the guided "connect your Claude" flow. Three screens: what you get →
// how to make a token (copyable commands) → paste + verify → try it.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {openKonvoAssistant} from '@app/features/konvo_connections/commands/KonvoAssistantCommands';
import styles from '@app/features/konvo_connections/components/KonvoClaudeConnectModal.module.css';
import {KonvoClaudeSignIn} from '@app/features/konvo_connections/components/KonvoClaudeSignIn';
import {useClaudeLink} from '@app/features/konvo_connections/hooks/useClaudeLink';
import KonvoConnections, {KonvoClaudeConnectError} from '@app/features/konvo_connections/state/KonvoConnections';
import {http} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {ClaudeLogo} from '@app/features/ui/components/icons/ClaudeLogo';
import {
	BrainIcon,
	CalendarCheckIcon,
	CheckCircleIcon,
	ChecksIcon,
	LockKeyIcon,
	SparkleIcon,
} from '@app/features/ui/icons/Icons';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {useCallback, useEffect, useState} from 'react';

const MODAL_KEY = 'konvo-claude-connect';
const INSTALL_COMMAND_MAC = 'curl -fsSL https://claude.ai/install.sh | bash';
const INSTALL_COMMAND_WINDOWS = 'irm https://claude.ai/install.ps1 | iex';
const TOKEN_COMMAND = 'claude setup-token';
const CLAUDE_CODE_DOCS_URL = 'https://code.claude.com/docs/en/setup';

const TITLE_DESCRIPTOR = msg({message: 'Connect Claude to your Assistant', comment: 'Claude connect modal title.'});
const NEXT_DESCRIPTOR = msg({message: 'Next', comment: 'Claude connect modal next button.'});
const BACK_DESCRIPTOR = msg({message: 'Back', comment: 'Claude connect modal back button.'});
const MANUAL_DESCRIPTOR = msg({
	message: 'Do it manually instead',
	comment: 'Claude connect modal link to the terminal-based path.',
});
const USE_SIGNIN_DESCRIPTOR = msg({
	message: 'Use the sign-in link instead',
	comment: 'Claude connect modal link back to the guided path.',
});
const CONNECT_DESCRIPTOR = msg({message: 'Verify & connect', comment: 'Claude connect modal submit button.'});
const VERIFYING_DESCRIPTOR = msg({
	message: 'Checking with Claude…',
	comment: 'Claude connect modal submit button while verifying.',
});
const DONE_DESCRIPTOR = msg({message: 'Done', comment: 'Claude connect modal close button on success.'});
const OPEN_ASSISTANT_DESCRIPTOR = msg({message: 'Open Assistant', comment: 'Claude connect modal success button.'});
const COPY_DESCRIPTOR = msg({message: 'Copy', comment: 'Copy a terminal command.'});
const COPIED_DESCRIPTOR = msg({message: 'Copied', comment: 'Shown briefly after copying a terminal command.'});
const TOKEN_LABEL_DESCRIPTOR = msg({message: 'Your token', comment: 'Label of the Claude token field.'});
const TOKEN_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'sk-ant-oat01-…',
	comment: 'Placeholder of the Claude token field.',
});
const STEP_LABEL_DESCRIPTOR = msg({
	message: 'Step {current} of {total}',
	comment: 'Progress label in the Claude connect modal.',
});
const CONNECTED_TOAST_DESCRIPTOR = msg({
	message: 'Claude connected. Your Assistant is ready.',
	comment: 'Toast after connecting Claude.',
});

const TRY_PROMPTS = [
	msg({message: "What's on my calendar today?", comment: 'Suggested first prompt for the Assistant.'}),
	msg({message: 'Summarise my unread mail', comment: 'Suggested first prompt for the Assistant.'}),
	msg({message: 'Which of my tasks are due this week?', comment: 'Suggested first prompt for the Assistant.'}),
	msg({message: 'Remind me at 16:00 to send the report', comment: 'Suggested first prompt for the Assistant.'}),
];

type Step = 'intro' | 'signin' | 'manual' | 'done';
const STEP_ORDER: ReadonlyArray<Step> = ['intro', 'signin'];

function CopyableCommand({command}: {command: string}) {
	const {i18n} = useLingui();
	const [copied, setCopied] = useState(false);
	const copy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(command);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard blocked: the command is still selectable text.
		}
	}, [command]);
	return (
		<div className={styles.command} data-flx="konvo-claude-connect.command">
			<code className={styles.commandText}>{command}</code>
			<button
				type="button"
				className={styles.copyButton}
				onClick={() => void copy()}
				data-flx="konvo-claude-connect.copy"
			>
				{i18n._(copied ? COPIED_DESCRIPTOR : COPY_DESCRIPTOR)}
			</button>
		</div>
	);
}

function Stepper({step}: {step: Step}) {
	const {i18n} = useLingui();
	const index = STEP_ORDER.indexOf(step);
	if (index === -1) return null;
	return (
		<div className={styles.stepper} data-flx="konvo-claude-connect.stepper">
			{STEP_ORDER.map((entry, i) => (
				<span
					key={entry}
					className={clsx(styles.stepDot, i === index && styles.stepDotActive, i < index && styles.stepDotDone)}
				/>
			))}
			<span className={styles.stepLabel}>
				{i18n._(STEP_LABEL_DESCRIPTOR, {current: index + 1, total: STEP_ORDER.length})}
			</span>
		</div>
	);
}

export function KonvoClaudeConnectModal({onClose}: {onClose: () => void}) {
	const {i18n} = useLingui();
	const [step, setStep] = useState<Step>('intro');
	const [token, setToken] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [runtimeMissing, setRuntimeMissing] = useState(false);
	// The sign-in link is prepared as soon as the modal opens so step two feels instant.
	const claude = useClaudeLink({
		onConnected: () => {
			ToastCommands.success(i18n._(CONNECTED_TOAST_DESCRIPTOR));
			setStep('done');
		},
	});
	const {link, starting: linkStarting, error: linkError, start: startLink} = claude;

	useEffect(() => {
		if (step === 'signin' && !link && !linkStarting && !linkError) void startLink();
	}, [step, link, linkStarting, linkError, startLink]);

	useEffect(() => {
		void http
			.get<{available: boolean; runtime: boolean}>(Endpoints.KONVO_CLAUDE_RUNTIME)
			.then((response) => setRuntimeMissing(response.body?.runtime === false))
			.catch(() => undefined);
	}, []);

	const submit = useCallback(async () => {
		setSubmitting(true);
		setError(null);
		try {
			await KonvoConnections.connectClaude(token.trim());
			ToastCommands.success(i18n._(CONNECTED_TOAST_DESCRIPTOR));
			setStep('done');
		} catch (err) {
			setError(err instanceof KonvoClaudeConnectError ? err.message : 'Could not connect Claude. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}, [token, i18n]);

	const tryPrompt = useCallback(
		(prompt: string) => {
			onClose();
			void openKonvoAssistant(prompt);
		},
		[onClose],
	);

	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-claude-connect.root">
			<Modal.Header title={i18n._(TITLE_DESCRIPTOR)} onClose={onClose} data-flx="konvo-claude-connect.header" />
			<Modal.Content data-flx="konvo-claude-connect.content">
				<div className={styles.content}>
					<Stepper step={step} />

					{step === 'intro' && (
						<>
							<div className={styles.hero}>
								<span className={styles.heroTile}>
									<ClaudeLogo size={28} />
								</span>
								<div>
									<p className={styles.heroTitle}>
										<Trans>Your own AI assistant, inside Konvo</Trans>
									</p>
									<p className={styles.heroText}>
										<Trans>
											Link the Claude subscription you already have. The Konvo Assistant DM then answers with your
											calendar, tasks and mail in hand, and remembers how you like things.
										</Trans>
									</p>
								</div>
							</div>
							<div className={styles.benefits}>
								<div className={styles.benefit}>
									<CalendarCheckIcon size={18} weight="fill" className={styles.benefitIcon} />
									<span className={styles.benefitTitle}>
										<Trans>Knows your day</Trans>
									</span>
									<span className={styles.benefitText}>
										<Trans>"What's on today?" "When am I free on Thursday?" It reads your Outlook calendar.</Trans>
									</span>
								</div>
								<div className={styles.benefit}>
									<ChecksIcon size={18} weight="fill" className={styles.benefitIcon} />
									<span className={styles.benefitTitle}>
										<Trans>Does the admin</Trans>
									</span>
									<span className={styles.benefitText}>
										<Trans>Creates ClickUp tasks, marks them done and sets reminders from a sentence.</Trans>
									</span>
								</div>
								<div className={styles.benefit}>
									<SparkleIcon size={18} weight="fill" className={styles.benefitIcon} />
									<span className={styles.benefitTitle}>
										<Trans>Summarises anything</Trans>
									</span>
									<span className={styles.benefitText}>
										<Trans>Unread mail, the week ahead, a long thread you paste in.</Trans>
									</span>
								</div>
								<div className={styles.benefit}>
									<BrainIcon size={18} weight="fill" className={styles.benefitIcon} />
									<span className={styles.benefitTitle}>
										<Trans>Remembers you</Trans>
									</span>
									<span className={styles.benefitText}>
										<Trans>Your preferences and context stay with your assistant, and only yours.</Trans>
									</span>
								</div>
							</div>
							<p className={styles.fineprint}>
								<LockKeyIcon size={12} weight="fill" className={styles.fineprintIcon} />
								<Trans>
									Usage is billed to your own Claude subscription. Your token is stored encrypted, your conversations
									run in an isolated space on the Konvo server, and you can disconnect at any time.
								</Trans>
							</p>
						</>
					)}

					{step === 'signin' && (
						<>
							<p className={styles.heroText}>
								<Trans>
									Nothing to install. Open the sign-in link, approve Konvo on your Claude account, then paste the code
									it shows you below. Takes about a minute.
								</Trans>
							</p>
							{runtimeMissing && (
								<div className={styles.runtimeWarning} data-flx="konvo-claude-connect.runtime-warning">
									<Trans>
										The assistant runtime isn't installed on this Konvo server yet. Ask an admin to enable it.
									</Trans>
								</div>
							)}
							<KonvoClaudeSignIn claude={claude} onSubmit={() => void claude.submit()} />
							<div className={styles.fineprintRow}>
								<p className={styles.fineprint}>
									<LockKeyIcon size={12} weight="fill" className={styles.fineprintIcon} />
									<Trans>Konvo holds the sign-in open for ten minutes. The code is used once and never stored.</Trans>
								</p>
								<button
									type="button"
									className={styles.linkButton}
									onClick={() => {
										setError(null);
										claude.setError(null);
										setStep('manual');
									}}
									data-flx="konvo-claude-connect.go-manual"
								>
									{i18n._(MANUAL_DESCRIPTOR)}
								</button>
							</div>
						</>
					)}

					{step === 'manual' && (
						<>
							<p className={styles.heroText}>
								<Trans>
									If you would rather run the tool yourself, install Claude Code, generate a token in your terminal and
									paste it here.
								</Trans>
							</p>
							<div className={styles.steps}>
								<div className={styles.step}>
									<div className={styles.stepBody}>
										<span className={styles.stepTitle}>
											<Trans>Install Claude Code (no Node.js needed)</Trans>
										</span>
										<span className={styles.stepText}>
											<Trans>macOS or Linux, in Terminal:</Trans>
										</span>
										<CopyableCommand command={INSTALL_COMMAND_MAC} />
										<span className={styles.stepText}>
											<Trans>Windows, in PowerShell:</Trans>
										</span>
										<CopyableCommand command={INSTALL_COMMAND_WINDOWS} />
										<span className={styles.stepText}>
											<Trans>Other options are in the</Trans>{' '}
											<a
												href={CLAUDE_CODE_DOCS_URL}
												onClick={(event) => {
													event.preventDefault();
													void openExternalUrl(CLAUDE_CODE_DOCS_URL);
												}}
											>
												<Trans>Claude Code setup guide</Trans>
											</a>
											.
										</span>
									</div>
								</div>
								<div className={styles.step}>
									<div className={styles.stepBody}>
										<span className={styles.stepTitle}>
											<Trans>Generate your token</Trans>
										</span>
										<CopyableCommand command={TOKEN_COMMAND} />
										<span className={styles.stepText}>
											<Trans>
												A browser window asks you to sign in and approve. The terminal then prints a token starting with{' '}
												<code>sk-ant-oat01-</code>. Copy the whole thing.
											</Trans>
										</span>
									</div>
								</div>
								<div className={styles.step}>
									<div className={styles.stepBody}>
										<span className={styles.stepTitle}>
											<Trans>Paste it here</Trans>
										</span>
										<div className={styles.tokenField}>
											<Input
												label={i18n._(TOKEN_LABEL_DESCRIPTOR)}
												placeholder={i18n._(TOKEN_PLACEHOLDER_DESCRIPTOR)}
												value={token}
												autoComplete="off"
												spellCheck={false}
												onChange={(event) => {
													setToken(event.target.value);
													setError(null);
												}}
												onKeyDown={(event) => {
													if (event.key === 'Enter' && token.trim() && !submitting) void submit();
												}}
												data-flx="konvo-claude-connect.token"
											/>
											{error && (
												<span className={styles.error} data-flx="konvo-claude-connect.error">
													{error}
												</span>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className={styles.fineprintRow}>
								<p className={styles.fineprint}>
									<LockKeyIcon size={12} weight="fill" className={styles.fineprintIcon} />
									<Trans>Treat the token like a password: Konvo is the only place you should paste it.</Trans>
								</p>
								<button
									type="button"
									className={styles.linkButton}
									onClick={() => {
										setError(null);
										setStep('signin');
									}}
									data-flx="konvo-claude-connect.go-signin"
								>
									{i18n._(USE_SIGNIN_DESCRIPTOR)}
								</button>
							</div>
						</>
					)}

					{step === 'done' && (
						<div className={styles.success}>
							<CheckCircleIcon size={40} weight="fill" className={styles.successIcon} />
							<p className={styles.successTitle}>
								<Trans>You're connected</Trans>
							</p>
							<p className={styles.heroText}>
								<Trans>
									Message <strong>Konvo Assistant</strong> in your DMs any time. Try one of these to start:
								</Trans>
							</p>
							<div className={styles.tryList}>
								{TRY_PROMPTS.map((descriptor) => {
									const prompt = i18n._(descriptor);
									return (
										<button
											key={prompt}
											type="button"
											className={styles.tryChip}
											onClick={() => tryPrompt(prompt)}
											data-flx="konvo-claude-connect.try"
										>
											{prompt}
										</button>
									);
								})}
							</div>
							<p className={styles.fineprint}>
								<Trans>
									You can pause the assistant, change its model or edit what it remembers under Settings → Assistant.
								</Trans>
							</p>
						</div>
					)}
				</div>
			</Modal.Content>
			<Modal.Footer data-flx="konvo-claude-connect.footer">
				{step === 'intro' && (
					<>
						<Button variant="secondary" onClick={onClose} data-flx="konvo-claude-connect.cancel">
							{i18n._(CANCEL_DESCRIPTOR)}
						</Button>
						<Button onClick={() => setStep('signin')} data-flx="konvo-claude-connect.next">
							{i18n._(NEXT_DESCRIPTOR)}
						</Button>
					</>
				)}
				{step === 'signin' && (
					<>
						<Button
							variant="secondary"
							onClick={() => setStep('intro')}
							disabled={claude.submitting}
							data-flx="konvo-claude-connect.back"
						>
							{i18n._(BACK_DESCRIPTOR)}
						</Button>
						<Button
							onClick={() => void claude.submit()}
							disabled={!claude.canSubmit}
							submitting={claude.submitting}
							data-flx="konvo-claude-connect.submit-code"
						>
							{i18n._(claude.submitting ? VERIFYING_DESCRIPTOR : CONNECT_DESCRIPTOR)}
						</Button>
					</>
				)}
				{step === 'manual' && (
					<>
						<Button
							variant="secondary"
							onClick={() => setStep('signin')}
							disabled={submitting}
							data-flx="konvo-claude-connect.back"
						>
							{i18n._(BACK_DESCRIPTOR)}
						</Button>
						<Button
							onClick={() => void submit()}
							disabled={!token.trim() || submitting}
							submitting={submitting}
							data-flx="konvo-claude-connect.submit"
						>
							{i18n._(submitting ? VERIFYING_DESCRIPTOR : CONNECT_DESCRIPTOR)}
						</Button>
					</>
				)}
				{step === 'done' && (
					<>
						<Button variant="secondary" onClick={onClose} data-flx="konvo-claude-connect.done">
							{i18n._(DONE_DESCRIPTOR)}
						</Button>
						<Button onClick={() => tryPrompt('')} data-flx="konvo-claude-connect.open">
							{i18n._(OPEN_ASSISTANT_DESCRIPTOR)}
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
}

export function openKonvoClaudeConnectModal(): void {
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => <KonvoClaudeConnectModal onClose={() => ModalCommands.popWithKey(MODAL_KEY)} />),
		MODAL_KEY,
	);
}
